import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Award,
  GraduationCap,
  ExternalLink,
  Shield,
  Code2,
  TrendingUp,
  Globe,
  Server,
  Brain,
  Calendar,
  CheckCircle,
  RefreshCw
} from 'lucide-react';

const certifications = [
  {
    id: 1,
    title: "CCNP - Cisco Certified Network Professional",
    issuer: "Cisco Systems",
    date: "2020",
    icon: <Server />,
    color: "from-blue-600 to-cyan-500",
    description: "Advanced networking certification covering routing, switching, and troubleshooting",
    skills: ["Network Design", "Routing Protocols", "Network Security", "Troubleshooting"],
    verification: "https://www.cisco.com",
    level: "Advanced"
  },
  {
    id: 2,
    title: "Google Digital Marketing & E-commerce",
    issuer: "Google Career Certificates",
    date: "2023",
    icon: <TrendingUp />,
    color: "from-purple-600 to-pink-500",
    description: "Comprehensive digital marketing certification covering SEO, analytics, and e-commerce",
    skills: ["SEO", "Google Analytics", "Social Media", "E-commerce"],
    verification: "https://www.coursera.org/account/accomplishments/professional-cert/LP13BI3AVX99",
    level: "Professional"
  },
  {
    id: 3,
    title: "React.js Professional Certificate",
    issuer: "Meta",
    date: "2023",
    icon: <Code2 />,
    color: "from-cyan-600 to-blue-500",
    description: "Advanced React development certification covering hooks, context, and performance",
    skills: ["React Hooks", "Context API", "Performance", "Testing"],
    verification: "https://react.dev/",
    level: "Advanced"
  },
  {
    id: 4,
    title: "IBM AI Developer",
    issuer: "IBM",
    date: "2022",
    icon: <Brain />,
    color: "from-emerald-600 to-green-500",
    description: "Artificial Intelligence and machine learning certification for developers",
    skills: ["Machine Learning", "Python", "AI Ethics", "Neural Networks"],
    verification: "https://www.coursera.org/account/accomplishments/certificate/XUPMZR6P7IHK",
    level: "Intermediate"
  },
  {
    id: 5,
    title: "CCNA - Cisco Certified Network Associate",
    issuer: "Cisco Systems",
    date: "2019",
    icon: <Shield />,
    color: "from-orange-600 to-red-500",
    description: "Fundamental networking certification covering network basics and security",
    skills: ["Networking Basics", "IP Addressing", "Network Security", "Wireless"],
    verification: "https://www.cisco.com",
    level: "Associate"
  },
  {
    id: 6,
    title: "MSIT Masters in Infrastructure Technology",
    issuer: "University of Technology",
    date: "2021",
    icon: <GraduationCap />,
    color: "from-violet-600 to-purple-500",
    description: "Master's degree focusing on IT infrastructure, cloud computing, and security",
    skills: ["Cloud Computing", "Infrastructure", "Security", "Virtualization"],
    verification: "#",
    level: "Master"
  }
];

const CertificationCard = ({ cert, isFlipped, onClick, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative h-72 cursor-pointer"
      onClick={onClick}
    >
      {/* 3D Flip Container */}
      <div className="relative w-full h-full transition-transform duration-700" style={{
        transformStyle: 'preserve-3d',
        transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
      }}>
        {/* Front Side */}
        <div className="absolute inset-0 backface-hidden">
          <div className="p-6 rounded-2xl glass-effect border border-white/10 h-full flex flex-col">
            {/* Icon */}
            <div className={`p-3 rounded-xl bg-gradient-to-br ${cert.color} w-14 h-14 flex items-center justify-center mb-4`}>
              {cert.icon}
            </div>
            
            {/* Title */}
            <h3 className="text-lg font-bold mb-2 line-clamp-2">{cert.title}</h3>
            
            {/* Issuer & Date */}
            <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
              <span>{cert.issuer}</span>
              <span>{cert.date}</span>
            </div>
            
            {/* Level Badge */}
            <div className="mb-4">
              <span className={`px-3 py-1 text-xs rounded-full ${
                cert.level === "Advanced" ? "bg-blue-500/20 text-blue-400" :
                cert.level === "Professional" ? "bg-purple-500/20 text-purple-400" :
                cert.level === "Intermediate" ? "bg-green-500/20 text-green-400" :
                "bg-orange-500/20 text-orange-400"
              }`}>
                {cert.level}
              </span>
            </div>
            
            {/* Flip Hint */}
            <div className="mt-auto text-xs text-gray-500 flex items-center space-x-1">
              <RefreshCw className="w-3 h-3" />
              <span>Click to view details</span>
            </div>
          </div>
        </div>

        {/* Back Side */}
        <div className="absolute inset-0 backface-hidden rotate-y-180">
          <div className={`p-6 rounded-2xl bg-gradient-to-br ${cert.color} h-full flex flex-col`}>
            <div className="flex-1">
              {/* Close Button */}
              <div className="flex justify-end mb-4">
                <button className="text-white/70 hover:text-white">
                  <CheckCircle size={20} />
                </button>
              </div>
              
              {/* Description */}
              <p className="text-white/90 text-sm mb-4">{cert.description}</p>
              
              {/* Skills */}
              <div className="space-y-2">
                <h4 className="text-white font-semibold">Skills Covered:</h4>
                <div className="flex flex-wrap gap-2">
                  {cert.skills.map((skill, i) => (
                    <span key={i} className="px-2 py-1 text-xs bg-white/20 text-white rounded">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Verify Button */}
            <a
              href={cert.verification}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
            >
              <ExternalLink size={16} />
              <span className="text-sm">Verify Certificate</span>
            </a>
          </div>
        </div>
      </div>

      {/* Hover Effect */}
      <div className={`absolute -inset-4 bg-gradient-to-r ${cert.color} rounded-2xl blur-xl opacity-0 hover:opacity-20 transition-opacity duration-500 -z-10`}></div>
    </motion.div>
  );
};

const Certifications = () => {
  const [flippedCard, setFlippedCard] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  const filteredCerts = activeFilter === "all" 
    ? certifications 
    : certifications.filter(cert => 
        activeFilter === "tech" ? cert.title.includes("React") || cert.title.includes("AI") :
        activeFilter === "networking" ? cert.title.includes("Cisco") :
        cert.title.includes("Marketing") || cert.title.includes("Google")
      );

  return (
    <section id="certifications" className="py-20 relative">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary-900/5 via-transparent to-secondary-900/5"></div>
      <div className="absolute top-1/3 right-10 w-80 h-80 bg-primary-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/3 left-10 w-80 h-80 bg-secondary-500/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-effect border border-white/10 mb-4">
            <Award className="w-4 h-4 text-primary-400" />
            <span className="text-sm">Certifications</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Professional
            <span className="block gradient-text">Certifications</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Validated expertise across development, networking, and digital marketing
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8 flex justify-center"
        >
          <div className="inline-flex rounded-full glass-effect p-1">
            {[
              { key: "all", label: "All Certifications" },
              { key: "tech", label: "Technology" },
              { key: "networking", label: "Networking" },
              { key: "marketing", label: "Marketing" }
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-4 py-2 rounded-full transition-all ${
                  activeFilter === filter.key
                    ? "bg-gradient-to-r from-primary-500 to-secondary-500"
                    : "hover:bg-white/10"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Certifications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredCerts.map((cert, index) => (
            <CertificationCard
              key={cert.id}
              cert={cert}
              isFlipped={flippedCard === cert.id}
              onClick={() => setFlippedCard(flippedCard === cert.id ? null : cert.id)}
              index={index}
            />
          ))}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {[
            { value: certifications.length, label: "Total Certifications", icon: <Award /> },
            { value: "Digital Marketing & Front-end Developer", label: "Highest Certification", icon: <Shield /> },
            { value: "2", label: "Certification Years", icon: <Calendar /> },
            { value: "100%", label: "Verified Credentials", icon: <CheckCircle /> },
          ].map((stat, index) => (
            <div
              key={index}
              className="p-6 rounded-2xl glass-effect border border-white/10 text-center"
            >
              <div className="inline-block p-3 rounded-xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 mb-4">
                {stat.icon}
              </div>
              <div className="text-2xl font-bold gradient-text">{stat.value}</div>
              <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* CSS for 3D Flip */}
      <style jsx>{`
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
};

export default Certifications;